//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import { type ApiClient } from './apiClient';
import {
  type AddBookmarkFolderResponse, type AddBookmarkResponse,
  type BookmarkData, type BookmarkFolder,
  type DeleteHistoryParams, type GetBookmarkResponse, type HistoryEntry
} from './apiTypes';

/**
 * History and bookmarks persistence: read/list/add/move/export/import/delete
 * bookmarks and read/delete history.
 */
export class BookmarkService {
  constructor(private readonly api: ApiClient) {}

  // Get a user's history for a specific category from the persistence layer.
  getHistory = (
    category: string,
    page: number | null = null,
    pageSize: number | null = null,
    offset = 0
  ): Promise<HistoryEntry[]> => {
    return this.api.makeRequest<HistoryEntry[]>(
      `history/get?category=${encodeURIComponent(category)}` +
      (page != null && pageSize != null && offset != null ?
        `&page=${page}&page_size=${pageSize}&offset=${offset}` : ''),
      'get');
  };

  getBookmark = (
    category: string, bookmarkType: string, id: string
  ): Promise<GetBookmarkResponse> => {
    return this.api.makeRequest<GetBookmarkResponse>(
      `bookmarks/get?category=${encodeURIComponent(category)}` +
      `&type=${encodeURIComponent(bookmarkType)}&id=${encodeURIComponent(id)}`,
      'get');
  };

  // Regular method (not an arrow field) so the folders-vs-data overload pair is
  // preserved; only ever called bound to the service, so `this` is safe.
  async getBookmarks(
    category: string, bookmarkType: 'bookmark_folders',
    path?: string | null, page?: number | null,
    page_size?: number | null
  ): Promise<BookmarkFolder[]>;
  async getBookmarks(
    category: string, bookmarkType: string,
    path?: string | null, page?: number | null,
    page_size?: number | null
  ): Promise<BookmarkData[]>;
  async getBookmarks(
    category: string,
    bookmarkType: string,
    path: string | null = null,
    page: number | null = null,
    page_size: number | null = null
  ): Promise<BookmarkData[] | BookmarkFolder[]> {
    let url = `bookmarks/list?category=${encodeURIComponent(category)}` +
              `&type=${encodeURIComponent(bookmarkType)}`;

    if (path != null)
      url += `&path=${encodeURIComponent(path)}`;

    if (page != null && page_size != null)
      url += `&page=${page}&page_size=${page_size}`;

    return this.api.makeRequest<BookmarkData[] | BookmarkFolder[]>(url, 'get');
  }

  addBookmarkFolder = (
    category: string, path: string
  ): Promise<AddBookmarkFolderResponse> => {
    return this.api.makeRequest<AddBookmarkFolderResponse>(
      'bookmarks/add_folder', 'post',
      { category, path });
  };

  addBookmark = (
    category: string,
    path: string,
    name: string,
    datasetName: string,
    originId: string | null = null,
    extraFields: Record<string, unknown> = {}
  ): Promise<AddBookmarkResponse> => {
    const url = 'bookmarks/add';
    const body = {
      category: category,
      path: path,
      name: name,
      origin_id: originId,
      dataset_name: datasetName,
      ...extraFields
    };

    return this.api.makeRequest<AddBookmarkResponse>(url, 'post', body);
  };

  moveBookmark = async (
    category: string,
    bookmarkType: string,
    id: string,
    name: string,
    path: string,
    content: unknown = null,
    unlink = false
  ): Promise<void> => {
    await this.api.makeRequest('bookmarks/move', 'post', {
      category, type: bookmarkType, id, name, path, content, unlink
    });
  };

  exportBookmarks = (
    category: string,
    folderPaths: string[] = [],
    bookmarkIds: string[] = []
  ): Promise<Blob> => {
    return this.api.makeRequest<Blob>(
      'bookmarks/export', 'post',
      { category, folder_paths: folderPaths, bookmark_ids: bookmarkIds },
      false, null, null, true
    );
  };

  exportAllBookmarks = (): Promise<Blob> => {
    return this.api.makeRequest<Blob>(
      'bookmarks/export_all', 'post', {}, false, null, null, true
    );
  };

  importBookmarks = (
    data: object
  ): Promise<{ imported?: number; skipped?: number }> => {
    return this.api.makeRequest<{ imported?: number; skipped?: number }>(
      'bookmarks/import', 'post', data);
  };

  deleteBookmark = async (
    category: string, bookmarkType: string, id: string
  ): Promise<void> => {
    await this.api.makeRequest('bookmarks/delete', 'post', {
      category, type: bookmarkType, id
    });
  };

  // The bookmark holding a query, if there is one.  Asked of the server rather than worked
  // out from a page of bookmarks, which can only be right about the page it fetched.
  matchBookmark = (
    category: string, bookmarkType: string, query: string,
    datasetName?: string
  ): Promise<{ item: BookmarkData | null }> => {
    return this.api.makeRequest<{ item: BookmarkData | null }>(
      'bookmarks/match', 'post',
      { category, type: bookmarkType, query, dataset_name: datasetName ?? null }
    );
  };

  // Record that a bookmark has just been opened, which is what its list is ordered by.
  touchBookmark = async (
    category: string, bookmarkType: string, id: string
  ): Promise<void> => {
    await this.api.makeRequest('bookmarks/touch', 'post', {
      category, type: bookmarkType, id
    });
  };

  // Delete entries from a user's history for a specific category in the persistence layer.
  deleteHistory = async (params: DeleteHistoryParams): Promise<void> => {
    await this.api.makeRequest('history/delete', 'put', params);
  };
}
